import { HorizontalScroll } from "@/components/HorizontalScroll";
import { Box, CarouselThumbnail } from "@biom3/react";
import { CarouselThumbnailContainer } from "@components/CarouselContainer/CarouselThumbnailContainer";

import { QuestTile } from "@components/QuestTiles/QuestTile/QuestTile";

import type {
  LatestEarnings,
  Quest,
  QuestTileType,
} from "@imx-tokens-ts/gems-game/domain";
import { controlCarouselVideosPlayback } from "@ui-kit/utils/videos";
import type { EmblaCarouselType } from "embla-carousel";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

export type TileProps = {
  data: Quest[];
  tileType: QuestTileType;
  latestEarnings?: LatestEarnings | undefined;
  scrollToIndex?: number;
  isLoading?: boolean;
  autoplay?: boolean;
  isThumbnail?: boolean;
  isAboveTheFold?: boolean;
};

export function Tiles({
  data,
  tileType,
  latestEarnings,
  scrollToIndex,
  isLoading,
  autoplay,
  isThumbnail,
  isAboveTheFold,
}: TileProps) {
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
  const videoDomRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const handleSlideChange = useCallback(async () => {
    const index = emblaApi?.selectedScrollSnap() || 0;
    await controlCarouselVideosPlayback(index, videoDomRefs.current);
  }, [emblaApi]);
  const handleInit = useCallback(async () => {
    const index = emblaApi?.selectedScrollSnap() || 0;
    await controlCarouselVideosPlayback(index, videoDomRefs.current);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    handleInit();
    emblaApi?.on("reInit", handleInit);
    emblaApi?.on("select", handleSlideChange);

    return () => {
      emblaApi?.off("select", handleSlideChange);
      emblaApi?.off("reInit", handleInit);
    };
  }, [emblaApi, handleInit, handleSlideChange]);

  return isThumbnail ? (
    <>
      <CarouselThumbnailContainer
        getApi={setEmblaApi}
        tileType={tileType}
        scrollToIndex={scrollToIndex}
        isLoading={isLoading}
        autoplay={autoplay}
        variant={"vertical"}
        thumbSpacing="base.spacing.x2"
      >
        {data.map((tileData, i) => (
          <CarouselThumbnail.Slide
            key={tileData.id}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              "& > .innerContentContainer": {
                p: "0px",
                width: "100%",
                height: "100%",
              },
            }}
          >
            <QuestTile
              quest={tileData}
              latestEarnings={latestEarnings}
              isPriority={i === 0}
            />
          </CarouselThumbnail.Slide>
        ))}
        {data.map((tileData, i) => (
          <CarouselThumbnail.Thumb
            key={tileData.id}
            sx={{
              backgroundColor: "base.color.translucent.inverse.800",
              backdropFilter: "blur(15px)",
            }}
          >
            <CarouselThumbnail.Thumb.Image
              use={
                <Image
                  src={tileData.logo.toString()}
                  alt={tileData.caption || "Tile image"}
                  width={64}
                  height={64}
                  style={{ objectFit: "cover", borderRadius: "4px" }}
                />
              }
            />
            <CarouselThumbnail.Thumb.Text>
              {tileData.caption}
            </CarouselThumbnail.Thumb.Text>
          </CarouselThumbnail.Thumb>
        ))}
      </CarouselThumbnailContainer>
      <Box sx={{ display: { default: "block", medium: "none" }, mt: "-48px" }}>
        <HorizontalScroll>
          {data.map((tileData, i) => (
            <QuestTile
              key={tileData.id}
              quest={tileData}
              latestEarnings={latestEarnings}
              isPriority={i === 0}
            />
          ))}
        </HorizontalScroll>
      </Box>
    </>
  ) : (
    <HorizontalScroll customScrollAmount={0.5}>
      {data.map((tileData, i) => (
        <QuestTile
          key={tileData.id}
          quest={tileData}
          latestEarnings={latestEarnings}
          isPriority={isAboveTheFold && i < 3}
        />
      ))}
    </HorizontalScroll>
  );
}
